import { collection, doc, getDoc, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import {
  FinanceAccountSchema,
  FinanceImportSchema,
  type FinanceAccount,
  type FinanceImport,
} from '@etiquetachef/types';
import { firestore } from './firebase';
import { reportNonFatal, serializeError } from './errorReport';

/**
 * Leituras do módulo Financeiro interno (épico #72). Coleções root
 * super-admin (`finance_accounts`, `finance_imports`) — leitura direta via
 * `onSnapshot` (rules gateiam por superAdmin). Escrita é só via callables.
 * Sem `orderBy` na query (ordena no cliente) → não exige índice.
 */
const TAG = 'admin/firestoreFinance';

export type FinanceAccountListResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; accounts: FinanceAccount[] }
  | { kind: 'ERROR'; error: unknown };

/**
 * Assina em tempo real as contas do razão interno (`finance_accounts`).
 *
 * Origem: #73 — 2026-06-21 19:44 +0200 (módulo Financeiro interno — menu,
 * Contas e Origens).
 *
 * Por quê: a lista é pequena (dezenas de contas) e muda raramente; o
 * `onSnapshot` deixa todas as telas do Financeiro coerentes depois de criar,
 * editar ou arquivar uma conta, sem refetch manual.
 *
 * Contrato: chama `onResult` com `READY` (contas válidas, ordenadas por
 * `name` com `localeCompare`, arquivadas incluídas — quem filtra é a tela)
 * ou `ERROR` (ex.: `permission-denied` sem claim de super-admin). Nunca
 * emite `LOADING` — o estado inicial é do chamador. Devolve o `Unsubscribe`.
 * Só leitura.
 *
 * Usado por (só `apps/admin`, web): `FinanceAccountsPage` (`/finance/accounts`),
 * `FinanceOverviewPage` (`/finance`), `FinanceCashflowPage`
 * (`/finance/cashflow`), `FinanceBalancePage` (`/finance/balance`),
 * `FinanceEntriesPage` (`/finance/entries`), `FinanceImportsPage`
 * (`/finance/imports`) e `FinanceImportFlowPage` (`/finance/imports/new` e
 * `/finance/imports/:importId`) — todas no menu "Financeiro".
 *
 * Arquitetura: regra `match /finance_accounts/{accountId}` (Lê: super-admin;
 * Escreve: só servidor — `createFinanceAccount`/`updateFinanceAccount`/
 * `archiveFinanceAccount`, capability `manageFinance`). Sem `orderBy` →
 * não exige índice composto.
 *
 * Dados: `finance_accounts/{accountId}` (`FinanceAccountSchema`,
 * `packages/types/src/finance.ts`):
 *   { "id": "acc-1", "name": "Conta principal", "bank": null,
 *     "kind": "BANK", "currency": "EUR", "timeZone": "Europe/Rome",
 *     "openingBalanceCents": 150000,     // CENTAVOS, pode ser negativo
 *     "archived": false, "createdAt": 1780000000000, "createdByUid": "uid-1",
 *     "updatedAt": 1780000000000, "updatedByUid": "uid-1" }
 * `kind` ∈ BANK | CASH (`bank` null em caixa). `timeZone` é legado-opcional:
 * doc antigo sem o campo recebe o default do schema (`Europe/Rome`).
 *
 * Dicas: doc que falha no schema some da lista e é reportado
 * (`admin_finance_parse_failed`, desde a #935 item 14 — antes só
 * `console.warn`, sem telemetria).
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export function subscribeFinanceAccounts(
  onResult: (r: FinanceAccountListResult) => void,
): Unsubscribe {
  const ref = collection(firestore, 'finance_accounts');
  return onSnapshot(
    ref,
    (snap) => {
      const accounts: FinanceAccount[] = [];
      for (const d of snap.docs) {
        const parsed = FinanceAccountSchema.safeParse(d.data());
        if (parsed.success) accounts.push(parsed.data);
        // #935 (item 14): era só `console.warn` — o doc sumia sem rastro.
        else reportNonFatal('admin_finance_parse_failed', { kind: 'account', docId: d.id, issues: JSON.stringify(parsed.error.issues).slice(0, 1000) });
      }
      accounts.sort((a, b) => a.name.localeCompare(b.name));
      onResult({ kind: 'READY', accounts });
    },
    (err) => {
      reportNonFatal('admin_finance_listener_failed', { tag: TAG, kind: 'accounts', ...serializeError(err) });
      onResult({ kind: 'ERROR', error: err });
    },
  );
}

export type FinanceImportListResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; imports: FinanceImport[] }
  | { kind: 'ERROR'; error: unknown };

/**
 * Assina o histórico das sessões de import de extrato por OCR (#75).
 *
 * Origem: #75 — 2026-06-21 22:25 +0200 (import de extrato bancário por
 * OCR/LLM com preview + dedupe).
 *
 * Por quê: a tela de histórico mostra a sessão mudar de `DRAFT` para
 * `CONFIRMED` assim que a callable confirma, sem refetch.
 *
 * Contrato: `onResult` recebe `READY` (sessões válidas, ordenadas por
 * `createdAt desc` no cliente) ou `ERROR`. Nunca emite `LOADING`. Devolve o
 * `Unsubscribe`. Só leitura; lê a coleção inteira, sem paginação.
 *
 * Usado por: `FinanceImportsPage` (rota `/finance/imports`, menu
 * Financeiro → "Importações"), só `apps/admin` (web).
 *
 * Arquitetura: regra `match /finance_imports/{importId}` (Lê: super-admin;
 * Escreve: só servidor — `parseBankStatementImage` cria,
 * `confirmStatementImport` confirma; capability `manageFinance`). Sem
 * `orderBy` na query → não exige índice.
 *
 * Dados: `finance_imports/{importId}` (`FinanceImportSchema`,
 * `packages/types/src/finance.ts`):
 *   { "id": "imp-1", "accountId": "acc-1",
 *     "imagePath": "finance_imports/imp-1/extrato.jpg",   // Storage
 *     "currency": "EUR",                // denormalizada da conta
 *     "status": "DRAFT",                // DRAFT | CONFIRMED
 *     "parsedCount": 12, "newCount": 10, "dupCount": 2,
 *     "confirmedCount": 0, "confirmedAt": null,  // null enquanto DRAFT
 *     "createdAt": 1780000000000, "createdByUid": "uid-1",
 *     "updatedAt": 1780000000000, "updatedByUid": "uid-1" }
 *
 * Dicas: sem limite na query — se o histórico crescer muito, paginar com
 * `orderBy('createdAt')` (índice single-field automático).
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export function subscribeFinanceImports(
  onResult: (r: FinanceImportListResult) => void,
): Unsubscribe {
  const ref = collection(firestore, 'finance_imports');
  return onSnapshot(
    ref,
    (snap) => {
      const imports: FinanceImport[] = [];
      for (const d of snap.docs) {
        const parsed = FinanceImportSchema.safeParse(d.data());
        if (parsed.success) imports.push(parsed.data);
        // #935 (item 14): era só `console.warn` — o doc sumia sem rastro.
        else reportNonFatal('admin_finance_parse_failed', { kind: 'import', docId: d.id, issues: JSON.stringify(parsed.error.issues).slice(0, 1000) });
      }
      imports.sort((a, b) => b.createdAt - a.createdAt);
      onResult({ kind: 'READY', imports });
    },
    (err) => {
      reportNonFatal('admin_finance_listener_failed', { tag: TAG, kind: 'imports', ...serializeError(err) });
      onResult({ kind: 'ERROR', error: err });
    },
  );
}

/**
 * Lê uma sessão de import pontual (para reabrir um DRAFT).
 *
 * Origem: #75 — 2026-06-21 22:25 +0200.
 *
 * Contrato: devolve a sessão validada, ou `null` se o doc não existe ou
 * falha no `FinanceImportSchema` (reportado como `admin_finance_parse_failed`, #935). Erro do Firestore
 * (`permission-denied`, rede) é relançado. Mesmo doc e regra de
 * `subscribeFinanceImports`.
 *
 * Usado por: `FinanceImportFlowPage` (rota `/finance/imports/:importId`,
 * `loadReopenPreview`), só `apps/admin` (web).
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export async function getFinanceImport(importId: string): Promise<FinanceImport | null> {
  const snap = await getDoc(doc(firestore, 'finance_imports', importId));
  if (!snap.exists()) return null;
  const parsed = FinanceImportSchema.safeParse(snap.data());
  if (!parsed.success) {
    reportNonFatal('admin_finance_parse_failed', { kind: 'import', docId: importId, issues: JSON.stringify(parsed.error.issues).slice(0, 1000) });
    return null;
  }
  return parsed.data;
}
